import getConfig from 'next/config';
import { defaultPic, valueEmpty } from 'helpers';
import { type ImageLoader } from 'next/image';

const { CF_URL, CF_BUCKET } = getConfig().publicRuntimeConfig;

if (typeof btoa === 'undefined') {
  global.btoa = (str) => Buffer.from(str, 'binary').toString('base64');
}

export const cloudFlareLoader: ImageLoader = ({ src, width, quality }) => {
  /**
   * Images using the original src path have not been processed by Paperclip on
   * Rails and have not had their orientation rotated.
   *
   * This means we must check the path and apply the rotation when '/original/' appears in it.
   */
  const hasOriginalSrc = src.toLowerCase().indexOf('/original/') > -1;
  const maybeRotate = hasOriginalSrc ? { rotate: null } : {};

  let imageData: string;
  try {
    imageData = Buffer.from(
      JSON.stringify({
        key: decodeURIComponent(src),
        bucket: CF_BUCKET,
        edits: {
          webp: {
            quality,
          },
          resize: {
            width,
            height: undefined,
          },
          ...maybeRotate,
        },
      })
    ).toString('base64');
  } catch (e) {
    // eslint-disable-next-line
    console.log('Error getting image data', e);
    imageData = btoa(
      JSON.stringify({
        key: src,
        bucket: CF_BUCKET,
        edits: {
          webp: {
            quality,
          },
          resize: {
            width,
            height: undefined,
          },
          ...maybeRotate,
        },
      })
    );
  }

  return `${CF_URL}${imageData}?w=${width}`;
};

type NextOptimizedImageUrlParams = {
  src: string; // relative source image URL
  width?: number;
  quality?: number; // 1–100
};
export function nextOptimizedImageUrl({ src, width = 1200, quality = 100 }: NextOptimizedImageUrlParams): string {
  let basePath: string = getConfig().publicRuntimeConfig.RAILS_URL;
  // If basePath ends with a slash, remove it
  if (basePath.endsWith('/')) {
    basePath = basePath.slice(0, -1); // remove the last character
  }
  const imagesPath = `${basePath}/_next/image`;

  const result = new URL(imagesPath);
  result.searchParams.set('url', src);
  result.searchParams.set('w', width.toString());
  result.searchParams.set('q', quality.toString());

  return result.toString();
}

// TODO same is in new inbox in utils - make it common after release
export const determineStaticPreviewImage = (pictureUrl: string, firstName: string) => {
  if (pictureUrl?.includes('default_profile_pic.png')) {
    return defaultPic(firstName);
  }
  return pictureUrl;
};

export const getProfilePicFromUser = (user?: { picture?: { url?: string }; first_name?: string }) => {
  if (!user?.picture?.url?.includes('default_profile_pic.png')) {
    return user?.picture?.url;
  }
  return defaultPic(user?.first_name);
};

export const getLargePicFromUser = (user: { picture_large: { url: string }; first_name: string }) => {
  if (!valueEmpty(user?.picture_large?.url) && !user?.picture_large?.url.includes('default_profile_pic.png')) {
    return `${user?.picture_large?.url}`;
  }
  return defaultPic(user?.first_name);
};

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
  <defs>
    <linearGradient id="breathing-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#F5F7F9">
        <animate attributeName="stop-color" values="#F5F7F9;#DBDFE5;#F5F7F9" dur="2s" repeatCount="indefinite" />
      </stop>
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#breathing-gradient)" />
</svg>`;

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

// The coerced type is so it plays well with the Next.js Image component
export const getShimmerBase64 = (width: number, height: number) =>
  `data:image/svg+xml;base64,${toBase64(shimmer(width, height))}` as `data:image/${string}`;
